import { useState } from "react";
import { Mail, MessageCircle, Linkedin, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { CONTACT } from "../data/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="contact" className="bg-[#161617] text-white py-20 sm:py-28 scroll-mt-16" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#d4af37]">Contact</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
                Let's build your next<br />collection — properly.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-lg text-[#a1a1a6] leading-relaxed max-w-lg">
                Whether it's one season or a full design-function transformation, it starts with a conversation.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-9 flex flex-col gap-4 text-[15px]">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-white/85 hover:text-white transition-colors group">
                  <span className="w-10 h-10 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center group-hover:bg-[#0066cc] transition-colors"><Mail size={17} /></span>
                  {CONTACT.email}
                </a>
                <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 text-white/85 hover:text-white transition-colors group">
                  <span className="w-10 h-10 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center group-hover:bg-[#0066cc] transition-colors"><MessageCircle size={17} /></span>
                  WhatsApp {CONTACT.whatsapp}
                </a>
                <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-3 text-white/85 hover:text-white transition-colors group">
                  <span className="w-10 h-10 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center group-hover:bg-[#0066cc] transition-colors"><Linkedin size={17} /></span>
                  LinkedIn profile
                </a>
                <p className="flex items-center gap-3 text-white/60">
                  <span className="w-10 h-10 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center"><MapPin size={17} /></span>
                  {CONTACT.location}
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="glass-card rounded-[28px] p-8 sm:p-10 bg-white/[0.06] border-white/10" data-testid="contact-form">
              {sent ? (
                <div className="py-10 text-center">
                  <p className="text-lg font-semibold">Message sent</p>
                  <p className="mt-2 text-sm text-[#a1a1a6]">Thank you — Sundeepa will get back to you as soon as possible.</p>
                </div>
              ) : (
                <>
                  <p className="text-lg font-semibold">Or just leave a message</p>
                  <div className="mt-6 flex flex-col gap-4">
                    <input
                      required placeholder="Your name" data-testid="contact-form-name-input"
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="px-5 py-3.5 rounded-2xl bg-white/[0.07] border border-white/15 text-white placeholder-white/40 text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                    />
                    <input
                      required type="email" placeholder="Email" data-testid="contact-form-email-input"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="px-5 py-3.5 rounded-2xl bg-white/[0.07] border border-white/15 text-white placeholder-white/40 text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                    />
                    <textarea
                      required rows={4} placeholder="Tell her about your brand or project" data-testid="contact-form-message-input"
                      value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="px-5 py-3.5 rounded-2xl bg-white/[0.07] border border-white/15 text-white placeholder-white/40 text-[15px] outline-none focus:border-[#0066cc] transition-colors resize-none"
                    />
                  </div>
                  {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
                  <button
                    type="submit" disabled={sending} data-testid="contact-form-submit-btn"
                    className="mt-6 w-full px-6 py-4 rounded-full bg-white text-[#1d1d1f] font-medium text-sm hover:bg-[#e8e8ed] transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                    {sending ? "Sending..." : "Send message"}
                  </button>
                </>
              )}
            </form>
          </Reveal>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[13px] text-white/50">
          <p>© {new Date().getFullYear()} Sundeepa Bamval — Fashion Design Consultancy</p>
          <a href="/admin" data-testid="footer-admin-link" className="hover:text-white transition-colors">Studio Login</a>
        </div>
      </div>
    </footer>
  );
}
